import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/auth/AuthContext';
import { Layout } from '@/components/Layout';
import { RequireAuth } from '@/components/RequireAuth';
import { Login } from '@/pages/Login';
import { Register } from '@/pages/Register';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { ResetPassword } from '@/pages/ResetPassword';
import { Chat } from '@/pages/Chat';
import { Connectors } from '@/pages/Connectors';
import { Brain } from '@/pages/Brain';
import { Plans } from '@/pages/Plans';
import { OnboardingCheckout } from '@/pages/OnboardingCheckout';
import { Admin } from '@/pages/Admin';
import { NotFound } from '@/pages/NotFound';

/**
 * Root of the Brew AI React client.
 *
 * Chat is the landing page — every authenticated visit lands directly
 * on the chat UI. The old "system status" Home page has been removed.
 *
 * Routes:
 *   /login         — sign in   (public)
 *   /register      — create account (public)
 *   /forgot-password — request password reset (public)
 *   /reset-password — input new password (public)
 *   /              — redirect → /chat
 *   /chat          — full-viewport chat (rail + sidebar + main, no top nav)
 *   /connectors    — list + authorize connectors
 *   /brain         — wrapper catalog (telemetry)
 *   /admin         — admin console
 */
export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public auth pages — no Layout shell, full-bleed forms */}
          <Route path="/login"    element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-password.html" element={<ResetPassword />} />

          {/* Root → chat. Chat is the only landing experience. */}
          <Route path="/"     element={<Navigate to="/chat" replace />} />

          {/* Chat owns the full viewport (rail + sidebar + main) — no Layout shell. */}
          <Route path="/chat" element={<RequireAuth><Chat /></RequireAuth>} />

          {/* Post-signup pay-now/pay-later — full-bleed, reads the recorded intent. */}
          <Route path="/welcome" element={<RequireAuth><OnboardingCheckout /></RequireAuth>} />

          {/* Plans/checkout is a focused, full-bleed page (its own topbar + "back
              to chat" link) — no Layout shell, so no redundant second nav. */}
          <Route path="/plans" element={<RequireAuth><Plans /></RequireAuth>} />

          {/* Everything else uses the shared Layout (top nav). */}
          <Route element={<RequireAuth><Layout /></RequireAuth>}>
            <Route path="/connectors" element={<Connectors />} />
            <Route path="/brain"      element={<Brain />} />
            <Route path="/admin"      element={<Admin />} />
            <Route path="*"           element={<NotFound />} />
          </Route>

          <Route path="*" element={<Navigate to="/chat" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
